import {
  createAsyncThunk,
} from '@reduxjs/toolkit';

import type { ThunkApiType } from '@/store';
import type { AttendContactAbsenceJson } from '@/types/domain/attendContact/entities/attendContactAbsenceEntity';
import type { AttendContactLateJson } from '@/types/domain/attendContact/entities/attendContactLateEntity';
import type { AttendContactLeaveEarlyJson } from '@/types/domain/attendContact/entities/attendContactLeaveEarlyEntity';

import { attendContactSlice } from '@/domain/attendContact/redux';
import { uiSlice } from '@/domain/ui/redux';
import { service } from '@/share';

export const refreshAttendContactAbsenceList = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'attendContact/refreshAttendContactAbsenceList',
  async (_, thunkApi) => {
    const result = await service.attendContact.getAttendContactAbsenceList(null);
    thunkApi.dispatch(attendContactSlice.actions.setAttendContactAbsences(result));
  },
);

export const getAttendContactAbsenceList = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'attendContact/getAttendContactAbsenceList',
  async (_, thunkApi) => {
    const { nextTokenAbsence, attendContactAbsenceList } = thunkApi.getState().attendContact;
    const result = await service.attendContact.getAttendContactAbsenceList(nextTokenAbsence);
    const newList = attendContactAbsenceList.map((v) => v);
    for (let i = 0; i < result.items.length; i += 1) {
      newList.push(result.items[i]);
    }
    thunkApi.dispatch(attendContactSlice.actions.setAttendContactAbsences({
      items: newList,
      nextToken: result.nextToken,
    }));
  },
);

export const refreshAttendContactLateList = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'attendContact/refreshAttendContactLateList',
  async (_, thunkApi) => {
    const result = await service.attendContact.getAttendContactLateList(null);
    thunkApi.dispatch(attendContactSlice.actions.setAttendContactLates(result));
  },
);

export const getAttendContactLateList = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'attendContact/getAttendContactLateList',
  async (_, thunkApi) => {
    const { nextTokenLate, attendContactLateList } = thunkApi.getState().attendContact;
    const result = await service.attendContact.getAttendContactLateList(nextTokenLate);
    const newList = attendContactLateList.map((v) => v);
    for (let i = 0; i < result.items.length; i += 1) {
      newList.push(result.items[i]);
    }
    thunkApi.dispatch(attendContactSlice.actions.setAttendContactLates({
      items: newList,
      nextToken: result.nextToken,
    }));
  },
);

export const refreshAttendContactLeaveEarlyList = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'attendContact/refreshAttendContactLeaveEarlyList',
  async (_, thunkApi) => {
    const result = await service.attendContact.getAttendContactLeaveEarlyList(null);
    thunkApi.dispatch(attendContactSlice.actions.setAttendContactLeaveEarlys(result));
  },
);

export const getAttendContactLeaveEarlyList = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'attendContact/getAttendContactLeaveEarlyList',
  async (_, thunkApi) => {
    const { nextTokenLeaveEarly, attendContactLeaveEarlyList } = thunkApi.getState().attendContact;
    const result = await service.attendContact.getAttendContactLeaveEarlyList(nextTokenLeaveEarly);
    const newList = attendContactLeaveEarlyList.map((v) => v);
    for (let i = 0; i < result.items.length; i += 1) {
      newList.push(result.items[i]);
    }
    thunkApi.dispatch(attendContactSlice.actions.setAttendContactLeaveEarlys({
      items: newList,
      nextToken: result.nextToken,
    }));
  },
);

export const sendAttendContactAbsence = createAsyncThunk<
  void,
  AttendContactAbsenceJson,
  ThunkApiType
>(
  'attendContact/sendAttendContactAbsence',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      await service.attendContact.sendNurseryAttendContactAbsence(input);
      thunkApi.dispatch(uiSlice.actions.setMessage({
        key: 'info',
        content: '欠席連絡を送信しました',
      }));
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('wagako:goAttendHistory'));
      }, 0);
    }
  },
);

export const removeAttendContactAbsence = createAsyncThunk<
  void,
  AttendContactAbsenceJson,
  ThunkApiType
>(
  'attendContact/removeAttendContactAbsence',
  async (input, thunkApi) => {
    await service.attendContact.removeNurseryAttendContactAbsence(input);
    thunkApi.dispatch(uiSlice.actions.setMessage({
      key: 'info',
      content: '欠席連絡を撤回しました',
    }));
  },
);

export const sendAttendContactLate = createAsyncThunk<
  void,
  AttendContactLateJson,
  ThunkApiType
>(
  'attendContact/sendAttendContactLate',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      await service.attendContact.sendNurseryAttendContactLate(input);
      thunkApi.dispatch(uiSlice.actions.setMessage({
        key: 'info',
        content: '遅刻連絡を送信しました',
      }));
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('wagako:goAttendHistory'));
      }, 0);
    }
  },
);

export const removeAttendContactLate = createAsyncThunk<
  void,
  AttendContactLateJson,
  ThunkApiType
>(
  'attendContact/removeAttendContactLate',
  async (input, thunkApi) => {
    await service.attendContact.removeNurseryAttendContactLate(input);
    thunkApi.dispatch(uiSlice.actions.setMessage({
      key: 'info',
      content: '遅刻連絡を撤回しました',
    }));
  },
);

export const sendAttendContactLeaveEarly = createAsyncThunk<
  void,
  AttendContactLeaveEarlyJson,
  ThunkApiType
>(
  'attendContact/sendAttendContactLeaveEarly',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      await service.attendContact.sendNurseryAttendContactLeaveEarly(input);
      thunkApi.dispatch(uiSlice.actions.setMessage({
        key: 'info',
        content: '早退連絡を送信しました',
      }));
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('wagako:goAttendHistory'));
      }, 0);
    }
  },
);

export const removeAttendContactLeaveEarly = createAsyncThunk<
  void,
  AttendContactLeaveEarlyJson,
  ThunkApiType
>(
  'attendContact/removeAttendContactLeaveEarly',
  async (input, thunkApi) => {
    await service.attendContact.removeNurseryAttendContactLeaveEarly(input);
    thunkApi.dispatch(uiSlice.actions.setMessage({
      key: 'info',
      content: '早退連絡を撤回しました',
    }));
  },
);
